/**
 * Parse API timestamps that are stored as UTC in the database.
 * Accepts ISO with Z/offset, timezone-less strings (treated as UTC), Date, or epoch ms.
 * @param {string|number|Date|null|undefined} value
 * @returns {Date|null}
 */
export const parseUtcDate = (value) => {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  let raw = String(value).trim();
  if (!raw) return null;

  // PostgreSQL timestamptz short offset (+00, +02) → +00:00
  if (/[+-]\d{2}$/.test(raw) && !/[+-]\d{2}:\d{2}$/.test(raw)) {
    raw = raw.replace(/([+-]\d{2})$/, '$1:00');
  }

  // SQL "YYYY-MM-DD HH:mm:ss" → ISO date/time separator
  if (!raw.includes('T') && /^\d{4}-\d{2}-\d{2} /.test(raw)) {
    raw = raw.replace(' ', 'T');
  }

  // Already timezone-aware (Z or ±HH:MM / ±HHMM).
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  // SQL / ISO without offset → treat as UTC.
  const parsed = new Date(`${raw}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * @param {string|number|Date|null|undefined} value
 * @returns {number|null} epoch milliseconds
 */
export const parseUtcTimestamp = (value) => {
  const parsed = parseUtcDate(value);
  return parsed == null ? null : parsed.getTime();
};

/**
 * Format a UTC API timestamp in the browser's local timezone.
 * @param {string|number|Date|null|undefined} value
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string|null}
 */
export const formatUtcToLocal = (value, options) => {
  const parsed = parseUtcDate(value);
  if (parsed == null) return null;
  return options ? parsed.toLocaleString(undefined, options) : parsed.toLocaleString();
};

/** Browser IANA timezone (e.g. Europe/Sarajevo), or UTC if unavailable. */
export const getClientTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};
